import { useEffect, useRef, useState, createContext, useContext } from 'react';
import { Button, Image, Modal, Space } from 'antd';
import { useNavigate, useParams } from 'react-router';
import { useApp } from 'contexts/app';
import { useWorkSpace } from 'contexts/workspace';
import OrderService from 'services/order';
import OrderPayment from '../payment';
import OrderNoteModal from '../OrderNoteModal';
import OrderHistoryModal from '../OrderHistoryModal';
import useIsMobile from '@/hooks/useIsMobile';
import OrderDetailDesktop from './desktop';
import OrderDetailMobile from './mobile';
import '../table.css';

const OrderDetailContext = createContext();
const service = new OrderService();

const OrderDetail = () => {
  const isMobile = useIsMobile(992);
  const navigate = useNavigate();
  const { id } = useParams();
  const { workspace } = useWorkSpace();
  const { showLoading, prompt, printPdf } = useApp();
  const orderLimitStatus = workspace?.subscriptionStatus?.canCreateOrder;
  const hasAutoNotifiedRef = useRef(false);

  const [data, setData] = useState([]);
  const [customer, setCustomer] = useState({});
  const [lineItems, setLineItems] = useState([]);
  const [isModalPaymentProofOpen, setIsModalPaymentProofOpen] = useState(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [closeCooldown, setCloseCooldown] = useState(0);
  const [showOrderPayment, setShowOrderPayment] = useState(false);
  const [showOrderNoteModal, setShowOrderNoteModal] = useState(false);
  const [showOrderHistoryModal, setShowOrderHistoryModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    if (orderLimitStatus?.canCreate === false && !hasAutoNotifiedRef.current) {
      setIsLimitModalOpen(true);
      setCloseCooldown(10);
      hasAutoNotifiedRef.current = true;
    }

    if (orderLimitStatus?.canCreate === true) {
      hasAutoNotifiedRef.current = false;
      setIsLimitModalOpen(false);
      setCloseCooldown(0);
    }
  }, [id, orderLimitStatus?.canCreate]);

  useEffect(() => {
    if (!isLimitModalOpen) return;
    if (closeCooldown <= 0) return;
    const timer = setInterval(() => {
      setCloseCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [isLimitModalOpen, closeCooldown]);

  const closeLimitModal = () => {
    if (closeCooldown > 0) return;
    setIsLimitModalOpen(false);
  };

  const loadData = async () => {
    showLoading(true);
    const order = await service.get({ id });
    setData(order);
    setCustomer(order.customer || {});
    setLineItems(order.line_items || []);
    showLoading(false);
  };

  const createInvoice = async () => {
    showLoading(true);
    const result = await service.createInvoiceFile({ id });
    showLoading(false);
    if (result) {
      printPdf(result.invoice_file);
    }
  };

  const createPackingList = async () => {
    showLoading(true);
    const result = await service.createPackingListFile({ id });
    showLoading(false);
    if (result) {
      printPdf(result.file);
    }
  };

  const createShippingLabel = async () => {
    showLoading(true);
    const result = await service.createShippingLabelFile({ id });
    showLoading(false);
    if (result) {
      printPdf(result.file);
    }
  };

  const cancelOrder = async () => {
    prompt({
      title: 'Huỷ đơn',
      label: 'Nhập lý do huỷ',
      onOk: async (text) => {
        await service.cancel({ id, reason: text });
        loadData();
      },
    });
  };

  const finishOrder = async () => {
    await service.finish({ id, note: '' });
    loadData();
  };

  const viewModel = {
    data,
    customer,
    lineItems,
    id,
    navigate,
    isModalPaymentProofOpen,
    setIsModalPaymentProofOpen,
    showOrderPayment,
    setShowOrderPayment,
    showOrderNoteModal,
    setShowOrderNoteModal,
    showOrderHistoryModal,
    setShowOrderHistoryModal,
    cancelOrder,
    finishOrder,
    createInvoice,
    createPackingList,
    createShippingLabel,
  };

  return (
    <OrderDetailContext.Provider value={{ order: data, loadData }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {isMobile ? <OrderDetailMobile viewModel={viewModel} /> : <OrderDetailDesktop viewModel={viewModel} />}

        <Modal
          title="Chuyển khoản"
          open={isModalPaymentProofOpen}
          onOk={() => setIsModalPaymentProofOpen(false)}
          onCancel={() => setIsModalPaymentProofOpen(false)}
          footer={null}
        >
          {data.payment_proof?.image && <Image width={250} src={data.payment_proof?.image?.url} />}
        </Modal>

        <Modal
          title='Giới hạn đơn hàng'
          open={isLimitModalOpen}
          onCancel={closeLimitModal}
          closable={closeCooldown <= 0}
          maskClosable={closeCooldown <= 0}
          keyboard={closeCooldown <= 0}
          footer={[
            <Button key='cancel' onClick={closeLimitModal} disabled={closeCooldown > 0}>
              {closeCooldown > 0 ? `Đóng (${closeCooldown}s)` : 'Đóng'}
            </Button>,
            <Button
              key='upgrade'
              type='primary'
              onClick={() => {
                setIsLimitModalOpen(false);
                setCloseCooldown(0);
                navigate('/account/subscription');
              }}
            >
              Nâng cấp gói
            </Button>,
          ]}
        >
          <p>
            Bạn đã đạt giới hạn <strong>{orderLimitStatus?.limit}</strong> đơn hàng trong tháng này.
          </p>
          <p>
            Để có thể tạo thêm đơn hàng, vui lòng gia hạn hoặc nâng cấp gói cước của bạn.
          </p>
          <p>
            Bạn có thể đóng thông báo sau {closeCooldown > 0 ? `${closeCooldown} giây` : '0 giây'}.
          </p>
        </Modal>

        <OrderNoteModal orderID={id} open={showOrderNoteModal} onClose={() => setShowOrderNoteModal(false)} />
        <OrderPayment orderID={id} open={showOrderPayment} onClose={() => setShowOrderPayment(false)}></OrderPayment>
        <OrderHistoryModal open={showOrderHistoryModal} onClose={() => setShowOrderHistoryModal(false)} />
      </Space>
    </OrderDetailContext.Provider>
  );
};

const useOrderDetailScreen = () => {
  return useContext(OrderDetailContext);
};

export { useOrderDetailScreen };

export default OrderDetail;
